import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { webApi } from '../services/api';
import { isCanceledRequest } from '../utils/abortRequest';
import { formatPayoutListDateTime, formatPayoutTimelineDateTime } from '../utils/dateDisplay';

function formatPayoutRupee(n) {
  const x = Number(n || 0);
  return `₹${x.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function statusLabel(status) {
  const s = String(status || '').toUpperCase();
  if (s === 'SUCCESS') return 'Success';
  if (s === 'FAILED') return 'Failed';
  if (s === 'PENDING') return 'Pending';
  return status || '—';
}

function statusClass(status) {
  const s = String(status || '').toUpperCase();
  if (s === 'SUCCESS') return 'ph-badge ph-badge--success';
  if (s === 'FAILED') return 'ph-badge ph-badge--failed';
  if (s === 'PENDING') return 'ph-badge ph-badge--pending';
  return 'ph-badge ph-badge--muted';
}

function PersonIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" className="ph-inline-icon">
      <path
        fill="currentColor"
        d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
      />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" className="ph-inline-icon">
      <path
        fill="currentColor"
        d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7v-5z"
      />
    </svg>
  );
}

function CheckCircleIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" className="ph-badge-check">
      <path
        fill="currentColor"
        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
      />
    </svg>
  );
}

function SheetDetailRow({ label, value }) {
  return (
    <div className="ph-sheet-row">
      <dt>{label}</dt>
      <dd>{value != null && value !== '' ? value : '—'}</dd>
    </div>
  );
}

function PayoutDetailSheet({ transfer, onClose }) {
  const [copied, setCopied] = useState(false);
  const tid = transfer?.transferId || transfer?.id || '';

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const copyId = async () => {
    if (!tid) return;
    try {
      await navigator.clipboard.writeText(String(tid));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const tl = (v) => formatPayoutTimelineDateTime(v);

  const b = transfer?.beneficiary || {};

  return (
    <div className="ph-sheet-overlay" role="presentation" onClick={onClose}>
      <div className="ph-sheet" role="dialog" aria-modal="true" aria-labelledby="ph-sheet-title" onClick={(e) => e.stopPropagation()}>
        <div className="ph-sheet-head">
          <h2 id="ph-sheet-title" className="ph-sheet-title">
            Payout Details
          </h2>
          <button type="button" className="ph-sheet-close" onClick={onClose} aria-label="Close">
            <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
              <path
                fill="currentColor"
                d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
              />
            </svg>
          </button>
        </div>

        <div className="ph-sheet-hero">
          <p className="ph-sheet-amount">{formatPayoutRupee(transfer?.transferAmount)}</p>
          <span className={statusClass(transfer?.transferStatus)}>
            {String(transfer?.transferStatus || '').toUpperCase() === 'SUCCESS' ? <CheckCircleIcon /> : null}
            {statusLabel(transfer?.transferStatus)}
          </span>
        </div>

        <div className="ph-sheet-block">
          <h3 className="ph-sheet-block-title">Transfer Details</h3>
          <dl className="ph-sheet-dl">
            <SheetDetailRow label="Transfer ID" value={transfer?.transferId} />
            <SheetDetailRow label="Reference ID" value={transfer?.referenceId} />
            <SheetDetailRow label="Currency" value={transfer?.transferCurrency || 'INR'} />
          </dl>
        </div>

        <div className="ph-sheet-block">
          <h3 className="ph-sheet-block-title">Beneficiary Details</h3>
          <dl className="ph-sheet-dl">
            <SheetDetailRow label="Name" value={b.name} />
            <SheetDetailRow label="Bank" value={b.bankName} />
            <SheetDetailRow label="Account Number" value={b.bankAccountNumber} />
            <SheetDetailRow label="IFSC Code" value={b.bankIfsc} />
          </dl>
        </div>

        <div className="ph-sheet-block">
          <h3 className="ph-sheet-block-title">Timeline</h3>
          <dl className="ph-sheet-dl">
            <SheetDetailRow label="Initiated" value={tl(transfer?.initiatedAt)} />
            <SheetDetailRow label="Completed" value={tl(transfer?.completedAt)} />
          </dl>
        </div>

        <div className="ph-sheet-block">
          <h3 className="ph-sheet-block-title">Notes</h3>
          <p className="ph-sheet-notes">{transfer?.transferNotes || '—'}</p>
        </div>

        <button type="button" className="ph-sheet-copy" onClick={copyId}>
          {copied ? 'Copied' : 'Copy ID'}
        </button>
      </div>
    </div>
  );
}

const DATE_PERIODS = [
  { value: 'today', label: 'Today' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' }
];

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'SUCCESS', label: 'Success' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'PENDING', label: 'Pending' }
];

function PayoutHistoryPage() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState('today');
  const [status, setStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [transfers, setTransfers] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const ac = new AbortController();
    const { signal } = ac;
    (async () => {
      try {
        setLoading(true);
        const data = await webApi.getPayoutHistory(
          { period, status, limit: 50, offset: 0 },
          { signal }
        );
        if (signal.aborted) return;
        setTransfers(data?.transfers || []);
        setError('');
      } catch (err) {
        if (signal.aborted || isCanceledRequest(err)) return;
        setError(err.message || 'Failed to load payout history');
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [period, status]);

  return (
    <div className="ph-page">
      <header className="ph-topbar">
        <button type="button" className="ph-back" onClick={() => navigate(-1)} aria-label="Go back">
          <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="currentColor" d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
          </svg>
        </button>
        <h1 className="ph-topbar-title">Payout History</h1>
        <span className="ph-topbar-spacer" aria-hidden="true" />
      </header>

      <div className="ph-body">
        <section className="ph-filter-section">
          <p className="ph-filter-label">Filter by Date</p>
          <div className="ph-chips" role="group" aria-label="Date range">
            {DATE_PERIODS.map((p) => (
              <button
                key={p.value}
                type="button"
                className={`ph-chip${period === p.value ? ' ph-chip--active' : ''}`}
                onClick={() => setPeriod(p.value)}
              >
                {p.label}
              </button>
            ))}
          </div>
        </section>

        <section className="ph-filter-section">
          <p className="ph-filter-label">Filter by Status</p>
          <div className="ph-chips" role="group" aria-label="Status">
            {STATUS_FILTERS.map((s) => (
              <button
                key={s.value}
                type="button"
                className={`ph-chip${status === s.value ? ' ph-chip--active' : ''}`}
                onClick={() => setStatus(s.value)}
              >
                {s.label}
              </button>
            ))}
          </div>
        </section>

        {error ? (
          <p className="ph-banner ph-banner--error" role="alert">
            {error}
          </p>
        ) : null}

        {loading ? (
          <p className="ph-loading">Loading…</p>
        ) : transfers.length === 0 ? (
          <div className="ph-empty">
            <p className="ph-empty-title">No payouts</p>
            <p className="ph-empty-sub">No transfers match these filters.</p>
          </div>
        ) : (
          <ul className="ph-list">
            {transfers.map((item) => {
              const b = item.beneficiary || {};
              const bankLine = [b.bankName, b.maskedAccountNumber].filter(Boolean).join(' • ');
              return (
                <li key={item.id}>
                  <button type="button" className="ph-card" onClick={() => setSelected(item)}>
                    <div className="ph-card-top">
                      <span className="ph-card-amount">{formatPayoutRupee(item.transferAmount)}</span>
                      <span className={statusClass(item.transferStatus)}>
                        {String(item.transferStatus || '').toUpperCase() === 'SUCCESS' ? <CheckCircleIcon /> : null}
                        {statusLabel(item.transferStatus)}
                      </span>
                    </div>
                    <div className="ph-card-user">
                      <PersonIcon />
                      <span className="ph-card-name">{(b.name || '—').toUpperCase()}</span>
                    </div>
                    <p className="ph-card-bank">{bankLine || '—'}</p>
                    <div className="ph-card-when">
                      <CalendarIcon />
                      <span>{formatPayoutListDateTime(item.createdAt)}</span>
                    </div>
                    <p className="ph-card-id">ID: {item.transferId || item.id}</p>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {selected ? <PayoutDetailSheet transfer={selected} onClose={() => setSelected(null)} /> : null}
    </div>
  );
}

export default PayoutHistoryPage;
